import { useState, useRef, useEffect, useCallback } from 'react';
import { calculateTrimming, FontStyles } from './textUtils';

type CollapsibleTextProps = {
  text: string;
  maxLines?: number;
  charLimit?: number;
  fallbackText?: string;
  className?: string;
};

/**
 * CollapsibleText component that shows a truncated version of text with a "View more/less" toggle
 * @param {string} text - The text content to display
 * @param {number} maxLines - Maximum number of lines to show when collapsed (default: 2)
 * @param {number} charLimit - Minimum character count to show View more/less button (default: 100)
 */
export const CollapsibleText = ({
  text,
  maxLines = 2,
  charLimit = 65,
  fallbackText = 'Not Provided',
  className,
}: CollapsibleTextProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [truncatedText, setTruncatedText] = useState('');
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeTimeoutRef = useRef<number>();

  const performTruncation = useCallback(() => {
    if (!containerRef.current) return;

    const containerStyle = window.getComputedStyle(containerRef.current);
    const styles: FontStyles = {
      fontFamily: containerStyle.fontFamily,
      fontSize: containerStyle.fontSize,
      lineHeight: containerStyle.lineHeight,
    };

    const result = calculateTrimming(
      text,
      maxLines,
      containerRef.current.offsetWidth,
      styles,
      charLimit,
    );

    setNeedsTruncation(result.needsTruncation);
    setTruncatedText(result.truncatedText);
  }, [text, maxLines, charLimit]);

  const debouncedPerformTruncation = useCallback(() => {
    // Clear any existing timeout
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    // Use requestAnimationFrame to batch DOM reads/writes
    resizeTimeoutRef.current = window.setTimeout(() => {
      requestAnimationFrame(() => {
        performTruncation();
      });
    }, 100);
  }, [performTruncation]);

  useEffect(() => {
    if (!containerRef.current) return;

    // Initial calculation with delay to ensure layout
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(() => {
        performTruncation();
      });
    }, 100);

    // Recalculate on resize with debouncing
    const resizeObserver = new ResizeObserver(() => {
      debouncedPerformTruncation();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      clearTimeout(timeoutId);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [performTruncation, debouncedPerformTruncation]);

  if (!text) {
    return <span className="text-primary-100 text-[14px]">{fallbackText}</span>;
  }

  return (
    <div className={`${className} text-[14px] w-full`} ref={containerRef}>
      <span
        style={{
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          lineHeight: '1.5',
        }}
      >
        {isExpanded ? (
          <>
            {text}{' '}
            <button
              onClick={() => setIsExpanded(false)}
              className="text-blue-600 hover:text-blue-800 cursor-pointer underline hover:no-underline"
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                font: 'inherit',
                display: 'inline',
                textDecoration: 'underline',
                color: '#1890ff',
              }}
            >
              View less
            </button>
          </>
        ) : (
          <>
            {needsTruncation ? truncatedText : text}
            {needsTruncation && (
              <>
                ...{' '}
                <button
                  onClick={() => setIsExpanded(true)}
                  className="text-blue-600 hover:text-blue-800 cursor-pointer underline hover:no-underline"
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    display: 'inline',
                    textDecoration: 'underline',
                    color: '#1890ff',
                  }}
                >
                  View more
                </button>
              </>
            )}
          </>
        )}
      </span>
    </div>
  );
};

export default CollapsibleText;
